/** The document view's addresses and the small rules its screen follows. */
import type { DocumentView, PageTag, RowCell, TableRowView, TagRun } from "../lib/api";
import { enc } from "../lib/http";

/** The document view for one document of a package. Both segments are encoded. */
export function documentViewPath(pkg: string, id: string): string {
  return `/documents/${enc(pkg)}/doc/${enc(id)}`;
}

/** What a citation knows about its document: a path or the register's content key, and a page. */
export interface CitedDocument {
  path?: string | null;
  /** The register's key for the document's bytes: 32 hex characters (`document_key`). */
  key?: string | null;
  /** The package, when the citation knows it. Without it every package the reader may open is searched. */
  pkg?: string | null;
  page?: number | null;
}

const CONTENT_KEY = /^[0-9a-f]{32}$/;

/**
 * The address that opens a cited document in the document view, or null when the citation names
 * no document the view could find. The view is keyed by an id the citation does not carry, so this
 * address looks it up (`GET /documents/locate`) and then moves to the view at the cited page.
 */
export function documentOpenPath(c: CitedDocument): string | null {
  const q = new URLSearchParams();
  if (c.path) q.set("path", c.path);
  else if (c.key && CONTENT_KEY.test(c.key)) q.set("key", c.key);
  else return null;
  if (c.pkg) q.set("package", c.pkg);
  if (c.page && c.page > 0) q.set("page", String(c.page));
  return `/documents/open?${q.toString()}`;
}

/**
 * A local notice is cited as `file://<path>#page=N`, with the path as it is on disk, not URL
 * encoded (`vendor/notices.py`). A path can itself hold a `#` (`QA&#x3a;QC`), so only a trailing
 * `#page=N` is the page. Its path and page, or null for a web source.
 */
export function citedFile(url: string | null | undefined): { path: string; page: number | null } | null {
  if (!url || !url.startsWith("file://")) return null;
  const rest = url.slice("file://".length);
  const m = /^(.*)#page=(\d+)$/.exec(rest);
  const path = m ? m[1] : rest;
  if (!path) return null;
  const page = m ? Number.parseInt(m[2], 10) : 0;
  return { path, page: page > 0 ? page : null };
}

/** The page a trace citation's locator names ("page 4, box x=..."), or null. */
export function locatorPage(locator: string | null | undefined): number | null {
  const n = Number.parseInt(/^page (\d+)/.exec(locator ?? "")?.[1] ?? "", 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** A page number from the address, kept inside the document. */
export function pageFrom(raw: string | null, pageCount: number): number {
  const n = Number.parseInt(raw ?? "", 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return pageCount > 0 ? Math.min(n, pageCount) : n;
}

/** The tags read on one page, or none. */
export function tagsOn(doc: DocumentView, page: number): PageTag[] {
  return doc.pages.find((p) => p.page === page)?.tags ?? [];
}

/** What to say for a tag on a page that has no box drawn, or null when it is drawn. */
export function locationNote(t: PageTag): string | null {
  if (t.located === "not_recorded") {
    return `${t.tag}: tag found on this page; location not recorded, sweep again to mark it.`;
  }
  if (t.located === "not_found") {
    return `${t.tag}: tag found in this page's text; its words could not be placed on the page.`;
  }
  return null;
}

/** A tag's kind in words: the asset class its letters give, or none when they give none. */
export function kindLabel(kind: string): string {
  return kind && kind !== "unknown" ? kind.replace(/_/g, " ") : "kind not known";
}

/** How the sweep stands, in a sentence, when it does not explain the tag list itself. */
export function sweepNote(sweep: string, tagsShown = false): string | null {
  if (sweep === "none") {
    return tagsShown
      ? "No content sweep has been built for this package; the tags shown were read by the package tag run."
      : "No content sweep has been built for this package, so no tags are marked yet.";
  }
  if (sweep === "building") return "The content sweep is still reading this package; tags so far are shown.";
  if (sweep === "failed") return "The content sweep for this package failed; tags it read before failing are shown.";
  return null;
}

/** How a tag was found, in words. */
export function sourceLabel(source: string): string {
  const words: Record<string, string> = {
    content_sweep: "content sweep", page_ocr: "page read", table_read: "table row", index: "master index", pid: "P&ID",
  };
  return words[source] ?? source.replace(/_/g, " ");
}

/** The rows read from one page's table, in the order the table lists them. */
export function rowsOn(doc: DocumentView, page: number): TableRowView[] {
  return (doc.table_rows ?? []).filter((r) => r.page === page).sort((a, b) => a.row_index - b.row_index);
}

/** The cell an address names as `rowId:header`, on this page, or null. */
export function cellFrom(raw: string | null, doc: DocumentView, page: number): RowCell | null {
  if (!raw) return null;
  const at = raw.indexOf(":");
  if (at < 1) return null;
  const id = raw.slice(0, at), header = raw.slice(at + 1);
  const row = rowsOn(doc, page).find((r) => r.id === id);
  return row?.cells.find((c) => c.header === header) ?? null;
}

/** How the package tag run stands, in a sentence. */
export function tagRunNote(run: TagRun | null): string {
  if (!run) return "The tag run has not been run on this package: only the tags the content sweep read are marked.";
  if (run.status === "running") return `Reading every page for tags: ${run.documents_done} of ${run.documents_total} documents.`;
  if (run.status === "failed") return run.error ?? "The tag run failed.";
  const skipped = (run.table_pages_skipped ? ` ${run.table_pages_skipped} table pages were not read.` : "") +
    (run.documents_failed ? ` ${run.documents_failed} documents could not be opened.` : "") +
    (run.pages_beyond_limit ? ` ${run.pages_beyond_limit} pages past each document's first 12 were not read.` : "");
  const flagged = run.rows_flagged ? `, ${run.rows_flagged} flagged for review` : "";
  return `${run.tags_found} tags read on pages, ${run.tags_not_in_index} not in the index; ` +
    `${run.rows_verified} table rows read from ${run.table_pages} table pages${flagged} ` +
    `(${run.table_reads} Claude calls, ${run.table_reads_cached} from cache).${skipped}`;
}
